import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import useChemical from 'hooks/useChemical';
import type { CropRate, EnrichedChemical } from 'hooks/useChemical/types';
import useChemicalSearch from 'hooks/useChemicalSearch';
import type { ChemicalSearchResult } from 'hooks/useChemicalSearch/types';
import useMix from 'hooks/useMix';
import type { MixChemical } from 'hooks/useMix/types';
import usePageRoutes from 'hooks/usePageRoutes';

type ProductEnrichment = {
    cropNames: string[];
    cropRates: CropRate[];
    formulationName: string | null;
    genericMaxRate: number | null;
    genericMinRate: number | null;
    manufacturer: string | null;
    signalWord: string | null;
};

const deriveEnrichment = (
    enriched: EnrichedChemical | null,
    result: ChemicalSearchResult
): ProductEnrichment => ({
    cropNames: Array.from(
        new Set((enriched?.cropRates ?? []).map(rate => rate.crop).filter(Boolean))
    ),
    cropRates: enriched?.cropRates ?? [],
    formulationName: enriched?.formulationName ?? null,
    genericMaxRate: enriched?.genericMaxRate ?? null,
    genericMinRate: enriched?.genericMinRate ?? null,
    manufacturer: enriched?.manufacturer ?? result.manufacturer,
    signalWord: enriched?.signalWord ?? result.signalWord
});

const useData = () => {
    const { mixId } = useParams();
    const isEditing = Boolean(mixId);
    const { enrichChemical, fetchChemical } = useChemical();
    const {
        data: searchData,
        fetch,
        isLoading: isSearchLoading,
        results,
        search,
        totalCount
    } = useChemicalSearch();
    const { createMix, fetchMix, updateMix } = useMix();
    const { chemicalsPage } = usePageRoutes();
    const navigate = useNavigate();
    const [chemicals, setChemicals] = useState<MixChemical[]>([]);
    const [enrichmentById, setEnrichmentById] = useState<Record<string, ProductEnrichment>>({});
    const [mixName, setMixName] = useState('');
    const [crop, setCrop] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [targetPest, setTargetPest] = useState('');
    // Tracks chemicals whose rateUnit/ratePerUnit were manually picked by the
    // user so a later crop change doesn't clobber them.
    const [userUnitOverrides, setUserUnitOverrides] = useState<Set<string>>(new Set());
    // Chemicals inserted optimistically while enrichment is still in flight.
    // Their cards render a "Gathering label data…" skeleton until the
    // enrichment resolves and the chemical is patched with real data.
    const [pendingIds, setPendingIds] = useState<Set<string>>(new Set());

    useEffect(() => {
        if (!mixId) {
            return;
        }

        (async () => {
            const existing = await fetchMix({ id: mixId });

            if (!existing) {
                return;
            }

            setMixName(existing.name);
            setCrop(existing.crop ?? '');
            setTargetPest(existing.targetPest ?? '');
            setChemicals(existing.chemicals);

            const enrichedList = await Promise.all(
                existing.chemicals
                    .filter(chemical => chemical.chemicalType === 'ENRICHED')
                    .map(async chemical => ({
                        chemicalId: chemical.chemicalId,
                        enriched: await fetchChemical({ id: chemical.chemicalId })
                    }))
            );

            setEnrichmentById(previous => {
                const next = { ...previous };

                for (const entry of enrichedList) {
                    if (!entry.enriched) {
                        continue;
                    }

                    next[entry.chemicalId] = {
                        cropNames: Array.from(
                            new Set(entry.enriched.cropRates.map(rate => rate.crop).filter(Boolean))
                        ),
                        cropRates: entry.enriched.cropRates,
                        formulationName: entry.enriched.formulationName,
                        genericMaxRate: entry.enriched.genericMaxRate,
                        genericMinRate: entry.enriched.genericMinRate,
                        manufacturer: entry.enriched.manufacturer,
                        signalWord: entry.enriched.signalWord
                    };
                }

                return next;
            });
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mixId]);

    const handleSearch = async (query?: string): Promise<void> => {
        const target = (query ?? searchQuery).trim();

        if (target.length < 2) {
            return;
        }

        await search({ query: target });
    };

    const handleNext = async () => await fetch().next();

    const handleAddChemical = async (result: ChemicalSearchResult): Promise<void> => {
        const preliminaryId = result.id ?? result.agrianProductId ?? result.labelName;
        const isDuplicate = chemicals.some(chemical => chemical.chemicalId === preliminaryId);

        if (isDuplicate) {
            return;
        }

        // Optimistically insert a preliminary chemical from the search result
        // so the card appears immediately. Enrichment runs in the background
        // and patches the chemical with final data (including its real id).
        const preliminary: MixChemical = {
            applicationRate: 0,
            chemicalId: preliminaryId,
            chemicalType: 'ENRICHED',
            classification:
                result.classifications.find(classification => classification.primary)?.name ?? null,
            epaNumber: result.epaNumber,
            formulationCode: result.formulationCode,
            id: preliminaryId,
            labelName: result.labelName,
            ratePerUnit: 'acre',
            rateUnit: 'oz',
            sortOrder: chemicals.length + 1
        };

        setChemicals(previous => [...previous, preliminary]);

        setPendingIds(previous => {
            const next = new Set(previous);

            next.add(preliminaryId);

            return next;
        });

        const enriched = result.id
            ? await fetchChemical({ id: result.id })
            : result.agrianProductId
              ? await enrichChemical({ agrianProductId: result.agrianProductId })
              : null;

        if (!enriched) {
            console.error(
                `Could not add ${result.labelName} — enrichment failed (backend not reachable or product data unavailable).`
            );

            setChemicals(previous =>
                previous.filter(chemical => chemical.chemicalId !== preliminaryId)
            );

            setPendingIds(previous => {
                if (!previous.has(preliminaryId)) {
                    return previous;
                }

                const next = new Set(previous);

                next.delete(preliminaryId);

                return next;
            });

            return;
        }

        const cropMatch = crop ? enriched.cropRates.find(rate => rate.crop === crop) : undefined;

        setChemicals(previous =>
            previous.map(chemical =>
                chemical.chemicalId === preliminaryId
                    ? {
                          ...chemical,
                          applicationRate: cropMatch?.minRate ?? cropMatch?.maxRate ?? 0,
                          chemicalId: enriched.id,
                          classification:
                              enriched.classifications.find(
                                  classification => classification.primary
                              )?.name ?? chemical.classification,
                          epaNumber: enriched.epaNumber,
                          formulationCode: enriched.formulationCode,
                          id: enriched.id,
                          labelName: enriched.labelName,
                          ratePerUnit:
                              cropMatch?.ratePerUnit ??
                              enriched.ratePerUnit ??
                              chemical.ratePerUnit,
                          rateUnit: cropMatch?.rateUnit ?? enriched.rateUnit ?? chemical.rateUnit
                      }
                    : chemical
            )
        );

        setEnrichmentById(previous => ({
            ...previous,
            [enriched.id]: deriveEnrichment(enriched, result)
        }));

        setPendingIds(previous => {
            if (!previous.has(preliminaryId)) {
                return previous;
            }

            const next = new Set(previous);

            next.delete(preliminaryId);

            return next;
        });
    };

    const handleRateChange = ({ chemicalId, rate }: { chemicalId: string; rate: number }) => {
        setChemicals(previous =>
            previous.map(chemical =>
                chemical.chemicalId === chemicalId
                    ? { ...chemical, applicationRate: rate }
                    : chemical
            )
        );
    };

    const markUnitOverride = (chemicalId: string) => {
        setUserUnitOverrides(previous => {
            if (previous.has(chemicalId)) {
                return previous;
            }

            const next = new Set(previous);

            next.add(chemicalId);

            return next;
        });
    };

    const handleRateUnitChange = ({
        chemicalId,
        rateUnit
    }: {
        chemicalId: string;
        rateUnit: string;
    }) => {
        markUnitOverride(chemicalId);

        setChemicals(previous =>
            previous.map(chemical =>
                chemical.chemicalId === chemicalId ? { ...chemical, rateUnit } : chemical
            )
        );
    };

    const handleRatePerUnitChange = ({
        chemicalId,
        ratePerUnit
    }: {
        chemicalId: string;
        ratePerUnit: string;
    }) => {
        markUnitOverride(chemicalId);

        setChemicals(previous =>
            previous.map(chemical =>
                chemical.chemicalId === chemicalId ? { ...chemical, ratePerUnit } : chemical
            )
        );
    };

    // When the user picks a suggested crop, retarget every chemical's rate +
    // units to its per-crop label values. Products without a cropRate match
    // for the new crop are left alone.
    const handleCropChange = (nextCrop: string) => {
        setCrop(nextCrop);

        if (!nextCrop) {
            return;
        }

        setChemicals(previous =>
            previous.map(chemical => {
                const cropMatch = enrichmentById[chemical.chemicalId]?.cropRates.find(
                    rate => rate.crop === nextCrop
                );

                if (!cropMatch) {
                    return chemical;
                }

                // Respect user's manual unit picks — rate still retargets.
                if (userUnitOverrides.has(chemical.chemicalId)) {
                    return {
                        ...chemical,
                        applicationRate: cropMatch.minRate ?? cropMatch.maxRate ?? 0
                    };
                }

                return {
                    ...chemical,
                    applicationRate: cropMatch.minRate ?? cropMatch.maxRate ?? 0,
                    ratePerUnit: cropMatch.ratePerUnit ?? chemical.ratePerUnit,
                    rateUnit: cropMatch.rateUnit ?? chemical.rateUnit
                };
            })
        );
    };

    const handleRemove = ({ chemicalId }: { chemicalId: string }) => {
        setChemicals(previous => previous.filter(chemical => chemical.chemicalId !== chemicalId));

        setEnrichmentById(previous => {
            const { [chemicalId]: _removed, ...rest } = previous;

            return rest;
        });

        setPendingIds(previous => {
            if (!previous.has(chemicalId)) {
                return previous;
            }

            const next = new Set(previous);

            next.delete(chemicalId);

            return next;
        });

        setUserUnitOverrides(previous => {
            if (!previous.has(chemicalId)) {
                return previous;
            }

            const next = new Set(previous);

            next.delete(chemicalId);

            return next;
        });
    };

    const handleCancel = () => {
        navigate(chemicalsPage);
    };

    const handleSave = async (): Promise<void> => {
        if (!mixName.trim() || chemicals.length === 0 || pendingIds.size > 0) {
            return;
        }

        const body = {
            chemicals: chemicals.map(chemical => ({
                applicationRate: chemical.applicationRate,
                chemicalId: chemical.chemicalId,
                chemicalType: chemical.chemicalType,
                ratePerUnit: chemical.ratePerUnit,
                rateUnit: chemical.rateUnit,
                sortOrder: chemical.sortOrder
            })),
            crop: crop || null,
            name: mixName.trim(),
            targetPest: targetPest || null
        };

        const saved =
            isEditing && mixId
                ? await updateMix({ id: mixId, mix: body })
                : await createMix({ mix: body });

        if (saved) {
            navigate(chemicalsPage);
        }
    };

    const suggestedCrops = Array.from(
        new Set(chemicals.flatMap(chemical => enrichmentById[chemical.chemicalId]?.cropNames ?? []))
    ).sort();

    return {
        chemicals,
        crop,
        enrichmentById,
        handleAddChemical,
        handleCancel,
        handleNext,
        handleRateChange,
        handleRatePerUnitChange,
        handleRateUnitChange,
        handleRemove,
        handleSave,
        handleSearch,
        isEditing,
        isSearchLoading,
        mixName,
        pendingIds,
        results,
        searchData,
        searchQuery,
        setCrop: handleCropChange,
        setMixName,
        setSearchQuery,
        setTargetPest,
        suggestedCrops,
        targetPest,
        totalCount
    };
};

export default useData;
